import { useState, useEffect } from 'react';
import usePageTitle from 'hooks/usePageTitle';
import { useAlertCount } from 'components/shared/NotificationPanel';
import api from 'services/api';
import { formatDate as formatJour } from '../../../../utils/jour';

const EMPTY_MVT = { produit_id: '', type: 'entree', quantite: '', motif: '' };

const STATUT_CMD = {
  brouillon: { bg: 'rgba(0,0,0,0.04)', color: '#666' },
  validee: { bg: 'rgba(37,99,235,0.08)', color: '#2563EB' },
  en_cours: { bg: 'rgba(217,119,6,0.08)', color: '#D97706' },
  livree: { bg: 'rgba(26,135,84,0.08)', color: '#1A8754' },
  annulee: { bg: 'rgba(220,38,38,0.08)', color: '#DC2626' },
};

export default function Commerce() {
  const alertCount = useAlertCount();
  usePageTitle('Commerce & Stock', alertCount);

  const [tab, setTab] = useState('produits');
  const [produits, setProduits] = useState([]);
  const [commandes, setCommandes] = useState([]);
  const [mouvements, setMouvements] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showMvtForm, setShowMvtForm] = useState(false);
  const [mvtForm, setMvtForm] = useState(EMPTY_MVT);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      setLoading(true);
      const [pRes, cRes, mRes, sRes] = await Promise.all([
        api.get('/commerce/produits'),
        api.get('/commerce/commandes'),
        api.get('/commerce/mouvements'),
        api.get('/commerce/stats'),
      ]);
      setProduits(pRes.data);
      setCommandes(cRes.data);
      setMouvements(mRes.data);
      setStats(sRes.data);
    } catch {} finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const handleCreateMouvement = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post('/commerce/mouvements', { ...mvtForm, quantite: parseInt(mvtForm.quantite) });
      setShowMvtForm(false);
      setMvtForm(EMPTY_MVT);
      load();
    } catch {} finally { setSaving(false); }
  };

  const setMField = (k, v) => setMvtForm(f => ({ ...f, [k]: v }));
  const formatDate = (d) => d ? new Date(d).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '-';

  return (
    <div>
{stats && (
        <div className="stats-grid" style={{ marginBottom: '1.5rem' }}>
          <div className="stat-card">
            <div className="stat-card-header">
              <span className="stat-card-label">Produits actifs</span>
              <div className="stat-card-icon blue">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 16V8a2 2 0 00-1-1.73l-7-4a2 2 0 00-2 0l-7 4A2 2 0 003 8v8a2 2 0 001 1.73l7 4a2 2 0 002 0l7-4A2 2 0 0021 16z"/><polyline points="3.27,6.96 12,12.01 20.73,6.96"/><line x1="12" y1="22.08" x2="12" y2="12"/></svg>
              </div>
            </div>
            <div className="stat-card-value">{stats.produits.actifs}</div>
            <div className="stat-card-sub">{stats.produits.total} total</div>
          </div>
          <div className="stat-card">
            <div className="stat-card-header">
              <span className="stat-card-label">Valeur stock</span>
              <div className="stat-card-icon green">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6"/></svg>
              </div>
            </div>
            <div className="stat-card-value">{Number(stats.produits.valeur_stock || 0).toLocaleString()} F</div>
            <div className="stat-card-sub">CA ventes: {Number(stats.commandes.ca_ventes || 0).toLocaleString()} F</div>
          </div>
          {stats.produits.stock_bas > 0 && (
            <div className="stat-card">
              <div className="stat-card-header">
                <span className="stat-card-label">Stock bas</span>
                <div className="stat-card-icon orange">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
                </div>
              </div>
              <div className="stat-card-value" style={{ color: 'var(--gss-warning)' }}>{stats.produits.stock_bas}</div>
              <div className="stat-card-sub">produits sous seuil minimum</div>
            </div>
          )}
          <div className="stat-card">
            <div className="stat-card-header">
              <span className="stat-card-label">Commandes</span>
              <div className="stat-card-icon red">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="1" y="4" width="22" height="16" rx="2"/><line x1="1" y1="10" x2="23" y2="10"/></svg>
              </div>
            </div>
            <div className="stat-card-value">{stats.commandes.total}</div>
            <div className="stat-card-sub">{stats.commandes.en_cours} en cours — {stats.commandes.brouillons} brouillons</div>
          </div>
        </div>
      )}

      <div className="content-card">
        <div className="gss-toolbar">
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <button className={`gss-btn ${tab === 'produits' ? 'gss-btn-primary' : ''}`} onClick={() => setTab('produits')}>Produits ({produits.length})</button>
            <button className={`gss-btn ${tab === 'commandes' ? 'gss-btn-primary' : ''}`} onClick={() => setTab('commandes')}>Commandes ({commandes.length})</button>
            <button className={`gss-btn ${tab === 'mouvements' ? 'gss-btn-primary' : ''}`} onClick={() => setTab('mouvements')}>Mouvements ({mouvements.length})</button>
          </div>
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            {(tab === 'produits' || tab === 'mouvements') && <button className="gss-btn" onClick={() => { setMvtForm(EMPTY_MVT); setShowMvtForm(true); }}>+ Mouvement stock</button>}
          </div>
        </div>

        {loading ? (
          <p style={{ textAlign: 'center', padding: '2rem', color: 'var(--gss-text-light)' }}>Chargement...</p>
        ) : tab === 'produits' ? (
          produits.length === 0 ? (
            <p style={{ textAlign: 'center', padding: '2rem', color: 'var(--gss-text-light)' }}>Aucun produit enregistre</p>
          ) : (
            <div className="gss-table-wrap">
              <table className="gss-table">
                <thead><tr><th>Reference</th><th>Nom</th><th>Categorie</th><th>Prix achat</th><th>Prix vente</th><th>Stock</th><th>Statut</th></tr></thead>
                <tbody>
                  {produits.map(p => {
                    const stockBas = p.stock_actuel <= p.stock_minimum;
                    return (
                      <tr key={p.id}>
                        <td style={{ fontFamily: 'monospace', fontWeight: 600 }}>{p.reference}</td>
                        <td><strong>{p.nom}</strong>{p.fournisseur && <div style={{ color: 'var(--gss-text-secondary)', fontSize: '0.8rem' }}>{p.fournisseur}</div>}</td>
                        <td>{p.categorie || '-'}</td>
                        <td>{Number(p.prix_achat || 0).toLocaleString()} F</td>
                        <td>{Number(p.prix_vente || 0).toLocaleString()} F</td>
                        <td style={{ fontWeight: 600, color: stockBas ? '#DC2626' : 'var(--gss-text)' }}>
                          {p.stock_actuel} {p.unite}
                          {stockBas && <div style={{ fontSize: '0.75rem', color: '#DC2626' }}>Min: {p.stock_minimum}</div>}
                        </td>
                        <td>
                          <span className="gss-badge" style={{ background: p.actif ? 'rgba(26,135,84,0.08)' : 'rgba(0,0,0,0.04)', color: p.actif ? '#1A8754' : '#999' }}>
                            {p.actif ? 'Actif' : 'Inactif'}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )
        ) : tab === 'commandes' ? (
          commandes.length === 0 ? (
            <p style={{ textAlign: 'center', padding: '2rem', color: 'var(--gss-text-light)' }}>Aucune commande</p>
          ) : (
            <div className="gss-table-wrap">
              <table className="gss-table">
                <thead><tr><th>Numero</th><th>Type</th><th>Fournisseur/Client</th><th>Montant</th><th>Date</th><th>Statut</th></tr></thead>
                <tbody>
                  {commandes.map(c => {
                    const st = STATUT_CMD[c.statut] || STATUT_CMD.brouillon;
                    return (
                      <tr key={c.id}>
                        <td style={{ fontFamily: 'monospace', fontWeight: 600 }}>{c.numero}</td>
                        <td><span className="gss-badge" style={{ background: c.type === 'achat' ? 'rgba(37,99,235,0.08)' : 'rgba(26,135,84,0.08)', color: c.type === 'achat' ? '#2563EB' : '#1A8754' }}>{c.type === 'achat' ? 'Achat' : 'Vente'}</span></td>
                        <td>{c.fournisseur || '-'}</td>
                        <td style={{ fontWeight: 600 }}>{Number(c.montant_total || 0).toLocaleString()} F</td>
                        <td style={{ color: 'var(--gss-text-secondary)' }}>{formatJour(c.date_commande)}</td>
                        <td><span className="gss-badge" style={{ background: st.bg, color: st.color }}>{c.statut}</span></td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )
        ) : (
          mouvements.length === 0 ? (
            <p style={{ textAlign: 'center', padding: '2rem', color: 'var(--gss-text-light)' }}>Aucun mouvement de stock</p>
          ) : (
            <div className="gss-table-wrap">
              <table className="gss-table">
                <thead><tr><th>Date</th><th>Produit</th><th>Type</th><th>Quantite</th><th>Motif</th><th>Par</th></tr></thead>
                <tbody>
                  {mouvements.map(m => (
                    <tr key={m.id}>
                      <td style={{ color: 'var(--gss-text-secondary)', fontSize: '0.85rem', whiteSpace: 'nowrap' }}>{formatDate(m.created_at)}</td>
                      <td><strong>{m.produit_nom}</strong><div style={{ color: 'var(--gss-text-secondary)', fontSize: '0.8rem' }}>{m.produit_ref}</div></td>
                      <td>
                        <span className="gss-badge" style={{
                          background: (m.type === 'entree' || m.type === 'retour') ? 'rgba(26,135,84,0.08)' : 'rgba(220,38,38,0.08)',
                          color: (m.type === 'entree' || m.type === 'retour') ? '#1A8754' : '#DC2626'
                        }}>
                          {m.type === 'entree' ? 'Entree' : m.type === 'sortie' ? 'Sortie' : m.type === 'retour' ? 'Retour' : 'Ajustement'}
                        </span>
                      </td>
                      <td style={{ fontWeight: 600, color: (m.type === 'entree' || m.type === 'retour') ? '#1A8754' : '#DC2626' }}>
                        {(m.type === 'entree' || m.type === 'retour') ? '+' : '-'}{m.quantite}
                      </td>
                      <td style={{ color: 'var(--gss-text-secondary)' }}>{m.motif || '-'}</td>
                      <td>{m.prenom} {m.user_nom}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )
        )}
      </div>

      {showMvtForm && (
        <div className="gss-modal-overlay" onClick={() => setShowMvtForm(false)}>
          <div className="gss-modal gss-modal-lg" onClick={e => e.stopPropagation()}>
            <div className="gss-modal-header">
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#8B1A1A" strokeWidth="2"><circle cx="12" cy="12" r="10"/><circle cx="12" cy="12" r="6"/><circle cx="12" cy="12" r="2"/></svg>
                <h2>Mouvement de stock</h2>
              </div>
              <div className="gss-modal-subtitle">Suivi commercial</div>
              <button className="gss-modal-close" onClick={() => setShowMvtForm(false)}>&times;</button>
            </div>
            <form onSubmit={handleCreateMouvement}>
              <div className="gss-modal-section">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#8B1A1A" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 16V8a2 2 0 00-1-1.73l-7-4a2 2 0 00-2 0l-7 4A2 2 0 003 8v8a2 2 0 001 1.73l7 4a2 2 0 002 0l7-4A2 2 0 0021 16z"/><polyline points="3.27,6.96 12,12.01 20.73,6.96"/><line x1="12" y1="22.08" x2="12" y2="12"/>
                </svg>
                <span>SELECTION PRODUIT</span>
              </div>
              <div className="gss-form-grid">
                <div className="gss-field">
                  <label>Produit *</label>
                  <select className="gss-input" required value={mvtForm.produit_id} onChange={e => setMField('produit_id', e.target.value)}>
                    <option value="">-- Selectionner --</option>
                    {produits.filter(p => p.actif).map(p => <option key={p.id} value={p.id}>{p.reference} - {p.nom}</option>)}
                  </select>
                </div>
              </div>

              <div className="gss-modal-section">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#8B1A1A" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="23,6 13.5,15.5 8.5,10.5 1,18"/><polyline points="17,6 23,6 23,12"/>
                </svg>
                <span>DETAILS MOUVEMENT</span>
              </div>
              <div className="gss-form-grid">
                <div className="gss-field">
                  <label>Type *</label>
                  <select className="gss-input" value={mvtForm.type} onChange={e => setMField('type', e.target.value)}>
                    <option value="entree">Entree</option><option value="sortie">Sortie</option>
                    <option value="ajustement">Ajustement</option><option value="retour">Retour</option>
                  </select>
                </div>
                <div className="gss-field"><label>Quantite *</label><input className="gss-input" type="number" min="1" required value={mvtForm.quantite} onChange={e => setMField('quantite', e.target.value)} /></div>
                <div className="gss-field"><label>Motif</label><input className="gss-input" value={mvtForm.motif} onChange={e => setMField('motif', e.target.value)} /></div>
              </div>
              <div className="gss-modal-actions">
                <button type="button" className="gss-btn-cancel" onClick={() => setShowMvtForm(false)}>Annuler</button>
                <button type="submit" className="gss-btn-deploy" disabled={saving}>{saving ? 'Enregistrement...' : <><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M22 2L11 13"/><path d="M22 2L15 22l-4-9-9-4z"/></svg>Enregistrer</>}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
